import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarCheck, Maximize2, Camera } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { PlanCanvas } from "./PlanCanvas";
import { BookingDialog } from "./BookingDialog";
import { scrollToId } from "../lib/scroll";
import { PHOTOS } from "../data/photos";

const LINES = ["Build Your", "Dream Home in", "Woodlake Estates"];
const EASE = [0.22, 1, 0.36, 1];

const lineVariant = {
  hidden: { y: "115%" },
  visible: (i) => ({ y: "0%", transition: { duration: 1.1, delay: 0.35 + i * 0.16, ease: EASE } }),
};

const fade = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.9, ease: EASE },
});

const HeroBackground = () => (
  <div className="absolute inset-0">
    {PHOTOS.heroVideo && (
      <video
        data-testid="hero-bg-video"
        className="absolute inset-0 w-full h-full object-cover opacity-40"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster={PHOTOS.heroPoster}
      >
        {PHOTOS.heroVideoWebm && <source src={PHOTOS.heroVideoWebm} type="video/webm" />}
        <source src={PHOTOS.heroVideo} type="video/mp4" />
      </video>
    )}
    <div className="absolute inset-0 bg-forest/55" />
    <div className="absolute inset-0 opacity-[0.35]" style={{ background: "radial-gradient(ellipse 80% 60% at 20% 85%, #1f3d31 0%, transparent 60%)" }} />
    <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/35 to-transparent" />
  </div>
);

export const Hero = () => {
  const [fullSize, setFullSize] = useState(false);
  const [booking, setBooking] = useState(false);

  const onPinClick = (lot) => {
    if (lot.status === "Sold") return;
    scrollToId(`lot-card-${lot.id}`);
  };

  return (
    <section id="home" data-testid="hero-section" className="relative min-h-[94vh] flex items-center overflow-hidden bg-forest">
      <HeroBackground />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-20 lg:py-36 w-full grid lg:grid-cols-12 gap-12 lg:gap-14 items-center">
        <div className="lg:col-span-5">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }} className="font-sans text-xs tracking-[0.3em] uppercase text-brass font-semibold mb-8">
            Woodlake Estates · On the Lake Pontchartrain Levee
          </motion.p>

          <h1 className="font-serif font-light text-cream text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span custom={i} variants={lineVariant} initial="hidden" animate="visible" className="block">
                  {i === 2 ? <span className="italic text-brass">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p {...fade(1.0)} className="mt-8 max-w-xl font-sans text-base sm:text-lg leading-relaxed text-cream/85">
            Explore a limited collection of residential homesites — traditional street-front lots, a corner property, a
            cul-de-sac setting and a premier homesite along the Lake Pontchartrain levee.
          </motion.p>

          <motion.div {...fade(1.15)} className="mt-10">
            <button
              data-testid="hero-book-showing-btn"
              onClick={() => setBooking(true)}
              className="group relative inline-flex items-center gap-3 bg-brass text-forest font-sans text-base font-bold tracking-wide px-9 py-5 rounded-sm shadow-[0_0_0_0_rgba(184,154,88,0.6)] animate-cta-glow transition-[transform,background-color] [transition-duration:300ms] hover:bg-[#cbaa66] hover:-translate-y-0.5"
            >
              <CalendarCheck size={20} className="transition-transform duration-300 group-hover:rotate-[-8deg]" />
              Book a Private Showing
            </button>
            <p data-testid="hero-book-note" className="mt-3 font-sans text-xs tracking-[0.15em] uppercase text-brass/90">
              Free · Takes 30 seconds · We confirm within one business day
            </p>
          </motion.div>

          <motion.div {...fade(1.3)} className="mt-8 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-view-lots-btn"
              onClick={() => scrollToId("lots")}
              className="border border-cream/40 text-cream font-sans text-sm tracking-wide px-7 py-3.5 rounded-sm transition-colors duration-300 hover:bg-cream/10"
            >
              View Available Lots
            </button>
            <button
              data-testid="hero-contact-us-btn"
              onClick={() => scrollToId("contact")}
              className="border border-cream/40 text-cream font-sans text-sm tracking-wide px-7 py-3.5 rounded-sm transition-colors duration-300 hover:bg-cream/10"
            >
              Contact Us Now
            </button>
            <Link
              data-testid="hero-gallery-btn"
              to="/gallery"
              className="flex items-center gap-2 border border-cream/40 text-cream font-sans text-sm tracking-wide px-7 py-3.5 rounded-sm transition-colors duration-300 hover:bg-cream/10"
            >
              <Camera size={16} /> Photo Gallery
            </Link>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 1.1, ease: EASE }} className="lg:col-span-7">
          <div data-testid="hero-lot-plan" className="bg-cream p-3 sm:p-4 shadow-2xl shadow-black/30">
            <div className="flex items-center justify-between px-1 pb-3">
              <span className="font-sans text-[10px] sm:text-[11px] tracking-[0.25em] uppercase text-forest font-semibold">
                Interactive Lot Plan · Tap a lot to see its price
              </span>
              <span data-testid="hero-availability-callout" className="font-sans text-[10px] sm:text-[11px] tracking-[0.25em] uppercase text-brass font-semibold">
                7 Available · 1 Sold
              </span>
            </div>
            <div data-testid="lot-plan-canvas" className="border border-border bg-white overflow-hidden">
              <PlanCanvas onPinClick={onPinClick} compact />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 px-1 pt-3">
              <button
                data-testid="view-fullsize-plan-btn"
                onClick={() => setFullSize(true)}
                className="flex items-center gap-2 font-sans text-xs font-semibold tracking-wide text-forest hover:text-brass transition-colors duration-300"
              >
                <Maximize2 size={14} /> View Full-Size Lot Plan
              </button>
              <p className="font-sans text-[10px] text-forest/50">Use + / − to zoom · lot plan not to scale</p>
            </div>
          </div>
        </motion.div>
      </div>

      <Dialog open={fullSize} onOpenChange={setFullSize}>
        <DialogContent data-testid="fullsize-plan-modal" className="max-w-[96vw] w-[96vw] h-[92vh] bg-white p-2 rounded-sm">
          <DialogTitle className="sr-only">Full-size Woodlake Estates lot plan</DialogTitle>
          <PlanCanvas onPinClick={(lot) => { setFullSize(false); setTimeout(() => onPinClick(lot), 250); }} />
        </DialogContent>
      </Dialog>

      <BookingDialog open={booking} onOpenChange={setBooking} />
    </section>
  );
};
